export interface DraftMetadata {
  title?: string;
  description?: string;
}

const extractFromPlainText = (rawText: string): DraftMetadata => {
  const segments = rawText
    .split(/\n\s*\n/)
    .map(segment => segment.trim())
    .filter(segment => segment.length > 0);

  if (segments.length === 0) {
    return {};
  }

  const [first, ...rest] = segments;
  const metadata: DraftMetadata = {};

  if (first) {
    metadata.title = first;
  }

  if (rest.length > 0) {
    metadata.description = rest.join('\n\n');
  }

  return metadata;
};

export const parseDraftMetadata = (draftData?: string): DraftMetadata => {
  if (!draftData) {
    return {};
  }

  try {
    const parsed = JSON.parse(draftData);

    if (typeof parsed === 'string') {
      return extractFromPlainText(parsed);
    }

    if (Array.isArray(parsed)) {
      const metadata: DraftMetadata = {};

      const firstItemWithTitle = parsed.find(
        item =>
          item &&
          typeof item === 'object' &&
          typeof (item as { title?: unknown }).title === 'string'
      ) as { title?: string } | undefined;

      if (firstItemWithTitle?.title) {
        metadata.title = firstItemWithTitle.title;
      }

      const descriptionSegments = parsed
        .map(item => {
          if (typeof item === 'string') {
            return item;
          }

          if (item && typeof item === 'object') {
            const entry = item as Record<string, unknown>;
            if (typeof entry.content === 'string') {
              return entry.content;
            }
            if (typeof entry.text === 'string') {
              return entry.text;
            }
          }

          return '';
        })
        .map(segment => segment.trim())
        .filter(segment => segment.length > 0);

      if (descriptionSegments.length > 0) {
        metadata.description = descriptionSegments.join('\n\n');
      }

      return metadata;
    }

    if (parsed && typeof parsed === 'object') {
      const metadata: DraftMetadata = {};
      const record = parsed as Record<string, unknown>;

      const title = ['title', 'headline']
        .map(key =>
          typeof record[key] === 'string' ? (record[key] as string) : undefined
        )
        .find((value): value is string => Boolean(value));

      if (title) {
        metadata.title = title;
      }

      const description = ['description', 'summary', 'content', 'body']
        .map(key =>
          typeof record[key] === 'string' ? (record[key] as string) : undefined
        )
        .find((value): value is string => Boolean(value));

      if (description) {
        metadata.description = description;
      }

      return metadata;
    }
  } catch {
    const trimmed = draftData.trim();
    return trimmed ? extractFromPlainText(trimmed) : {};
  }

  const fallback = draftData.trim();
  return fallback ? extractFromPlainText(fallback) : {};
};
